
/**
 * Google Analytics and SEO Tracking Services
 * Optimized for modern browsers with async loading and proper user consent
 */
import { getGAMeasurementId, getGAdsConversionId, getGAdsConversionLabel } from './configService';

// Check for analytics consent
export const isAnalyticsAllowed = (): boolean => {
  try {
    const consent = localStorage.getItem('afsprakenmaker-cookie-consent');
    if (!consent) return false;
    
    const consentObj = JSON.parse(consent);
    return consentObj?.analytics === true;
  } catch (e) {
    console.error('Error checking analytics consent:', e);
    return false;
  }
};

// Wait for Leadinfo script to be ready
const waitForLeadinfo = (timeout = 5000): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if leadinfo is a function AND if it's fully initialized
    const isLeadinfoReady = () => {
      try {
        return typeof window.leadinfo === 'function' && 
               window.GlobalLeadinfoNamespace !== undefined;
      } catch {
        return false;
      }
    };

    if (isLeadinfoReady()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isLeadinfoReady()) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        resolve(false);
      }
    }, 100);
  });
};

// Track page view with Leadinfo (with proper loading check)
export const trackLeadinfoPageView = async (): Promise<void> => {
  if (!isAnalyticsAllowed()) return;

  try {
    const isReady = await waitForLeadinfo();
    if (!isReady) {
      return; // Silently fail if script didn't load
    }

    // Double-check that leadinfo is still a function
    if (typeof window.leadinfo !== 'function') {
      return;
    }

    // Try to call leadinfo with error handling
    try {
      window.leadinfo('page');
      console.log('✅ Leadinfo page view tracked');
    } catch (err) {
      // Silently handle the case where 'page' command is not available
      // This prevents the "page is not an available function" error
      console.warn('Leadinfo page tracking failed:', err);
    }
  } catch (error) {
    // Only warn, don't error - tracking failures shouldn't break the app
    console.warn('Could not track Leadinfo page view:', error);
  }
};

// Privacy Sandbox support detection
const isAttributionReportingSupported = (): boolean => {
  try {
    return 'Attribution-Reporting-Eligible' in document.head ||
           'Attribution-Reporting-Support' in document.head ||
           typeof (window as any).AttributionReporting === 'object';
  } catch (e) {
    return false;
  }
};

// Initialize Google Analytics - simplified version since GA4 is loaded via HTML
export const initializeGA = (): void => {
  const measurementId = getGAMeasurementId();
  
  if (!measurementId) {
    console.warn('Google Analytics Measurement ID not configured');
    return;
  }
  
  // GA4 script is loaded via HTML, just update consent if available
  if (window.gtag) {
    console.log('GA4 available - updating consent settings');
    
    // Update consent based on user preferences
    const isConsented = isAnalyticsAllowed();
    window.gtag('consent', 'update', {
      'analytics_storage': isConsented ? 'granted' : 'denied',
      'ad_storage': isConsented ? 'granted' : 'denied'
    });
    
    console.log('Google Analytics consent updated:', isConsented ? 'granted' : 'denied');
  } else {
    console.warn('GA4 not available - script may not have loaded');
  }
};

// Remove Google Analytics
export const removeGA = (): void => {
  if (window.gtag) {
    // Update consent to deny
    window.gtag('consent', 'update', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied'
    });
  }
  
  console.log('Google Analytics consent revoked');
};

// Handle consent changes for analytics
export const handleConsentChange = (preferences: { analytics: boolean, marketing: boolean }): void => {
  if (!window.gtag) {
    console.warn('gtag not available for consent update');
    return;
  }
  
  if (preferences.analytics) {
    window.gtag('consent', 'update', {
      'analytics_storage': 'granted'
    });
    console.log('Analytics consent granted');
  } else {
    window.gtag('consent', 'update', {
      'analytics_storage': 'denied'
    });
    console.log('Analytics consent denied');
  }
  
  if (preferences.marketing) {
    window.gtag('consent', 'update', {
      'ad_storage': 'granted'
    });
    console.log('Marketing consent granted');
  } else {
    window.gtag('consent', 'update', {
      'ad_storage': 'denied'
    });
    console.log('Marketing consent denied');
  }
};

// Track page views with optimized implementation
export const trackPageView = (path: string): void => {
  if (!window.gtag || !isAnalyticsAllowed()) {
    return;
  }
  
  try {
    // Use config update for page tracking - reduces Attribution API calls
    window.gtag('config', getGAMeasurementId(), {
      page_path: path,
      page_title: document.title,
      page_location: window.location.href
    });
    
    // Track in Ahrefs if available
    if (window.ahrefs) {
      window.ahrefs.trackPageview();
    }
    
    console.log('✅ GA4 page view tracked:', path);
    
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track conversions for specific events
export const trackConversion = (url?: string): boolean | undefined => {
  if (!window.gtag) {
    console.warn('gtag not available for conversion tracking');
    return false;
  }
  
  try {
    const conversionId = getGAdsConversionId();
    const conversionLabel = getGAdsConversionLabel();
    
    if (!conversionId || !conversionLabel) {
      console.warn('Google Ads conversion tracking not properly configured');
      return false;
    }

    window.gtag('event', 'conversion', {
      'send_to': `${conversionId}/${conversionLabel}`,
      'event_callback': url ? () => {
        window.location.href = url;
      } : undefined
    });
    
    console.log('Conversion tracked');
    return true;
  } catch (error) {
    console.error('Error tracking conversion:', error);
    return false;
  }
};

// Track external link clicks for SEO monitoring
export const trackExternalLinkClick = (url: string, linkText: string): void => {
  if (!window.gtag) return;
  
  try {
    window.gtag('event', 'click', {
      'event_category': 'External Link',
      'event_label': linkText || url,
      'value': 1,
      'link_url': url
    });
    
    console.log('External link click tracked:', url);
  } catch (error) {
    console.error('Error tracking external link click:', error);
  }
};

// Track internal link clicks for navigation analysis
export const trackInternalLinkClick = (destination: string, linkText: string): void => {
  if (!window.gtag) return;
  
  try {
    window.gtag('event', 'click', {
      'event_category': 'Internal Link',
      'event_label': linkText,
      'value': 1,
      'page_path': destination
    });
    
    console.log('Internal link click tracked:', destination);
  } catch (error) {
    console.error('Error tracking internal link click:', error);
  }
};

// Track CTA button clicks
export const trackCTAClick = (ctaId: string, ctaText: string): void => {
  if (!window.gtag) return;
  
  try {
    window.gtag('event', 'click', {
      'event_category': 'CTA',
      'event_label': ctaId,
      'event_action': ctaText,
      'value': 5 // CTAs are more valuable than regular links
    });
    
    // Also track as conversion for important CTAs
    if (ctaId.includes('contact') || ctaId.includes('quote')) {
      window.gtag('event', 'generate_lead', {
        'currency': 'EUR',
        'value': 100,
        'event_category': 'Lead Generation'
      });
    }
    
    console.log('CTA click tracked:', ctaId);
  } catch (error) {
    console.error('Error tracking CTA click:', error);
  }
};

// Rate limiting for timing events to prevent Attribution API spam
let lastTimingTrack = 0;
const TIMING_THROTTLE = 5000; // 5 seconds

// Track SEO metrics with optimized frequency
export const trackSEOMetrics = (): void => {
  if (!window.gtag || !isAnalyticsAllowed()) return;
  
  // Throttle timing events to reduce Attribution API calls
  const now = Date.now();
  if (now - lastTimingTrack < TIMING_THROTTLE) {
    return;
  }
  lastTimingTrack = now;
  
  try {
    // Use performance API with proper error handling
    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      
      // Only track if timing is complete
      if (timing.loadEventEnd > 0) {
        const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
        const domLoadTime = timing.domContentLoadedEventEnd - timing.navigationStart;
        
        // Single consolidated timing event to reduce API calls
        window.gtag('event', 'page_performance', {
          'page_load_time': pageLoadTime,
          'dom_load_time': domLoadTime,
          'event_category': 'Performance',
          'non_interaction': true
        });
        
        console.log('Performance metrics tracked - Load:', pageLoadTime, 'DOM:', domLoadTime);
      }
    }
  } catch (error) {
    console.error('Error tracking SEO metrics:', error);
  }
};

// Test function to verify tracking is working
export const testGA4Tracking = (): void => {
  if (!window.gtag) {
    console.error('GA4 not available for testing');
    return;
  }
  
  console.log('Testing GA4 tracking...');
  
  // Send test events
  window.gtag('event', 'test_event', {
    'event_category': 'Debug',
    'event_label': 'GA4_Test',
    'value': 1,
    'debug_mode': true
  });
  
  window.gtag('event', 'page_view', {
    'page_title': 'Test Page View',
    'page_location': window.location.href,
    'send_to': getGAMeasurementId()
  });
  
  console.log('Test events sent to GA4');
  console.log('Check Google Analytics Real-Time reports to verify tracking');
};

// Add to window for debugging
if (typeof window !== 'undefined') {
  (window as any).testGA4 = testGA4Tracking;
}
